import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const LAST_UPDATED = 'March 26, 2026';
const COMPANY = 'AdForge AI';
const CONTACT_EMAIL = 'privacy@adforge.ai';
const WEBSITE = 'https://adforge.ai';

const sections = [
  {
    id: 'information-we-collect',
    title: '1. Information We Collect',
    content: [
      {
        subtitle: 'Account Information',
        text: 'When you create an account, we collect your name, email address, and password. If you subscribe to a paid plan, billing information (handled securely by our payment processor, Stripe) is also collected.',
      },
      {
        subtitle: 'Business Profile Data',
        text: 'To generate ads, you may provide us with information about your business, including business name, industry, target audience, tone preferences, and promotional details. This data is used solely to power your ad generation.',
      },
      {
        subtitle: 'Usage Data',
        text: 'We automatically collect information about how you interact with AdForge AI, including pages visited, features used, ad generations performed, session duration, and device/browser information.',
      },
      {
        subtitle: 'Communications',
        text: 'If you contact our support team or respond to our emails, we retain those communications to assist you and improve our service.',
      },
    ],
  },
  {
    id: 'how-we-use',
    title: '2. How We Use Your Information',
    bullets: [
      'Provide, operate, and improve the AdForge AI platform',
      'Generate AI-powered ad copy and creative images on your behalf',
      'Process payments and manage your subscription',
      'Send transactional emails (receipts, password resets, usage alerts)',
      'Send product updates and marketing communications (you can opt out at any time)',
      'Detect fraud, abuse, or violations of our Terms of Service',
      'Comply with legal obligations',
    ],
  },
  {
    id: 'data-sharing',
    title: '3. Data Sharing & Third Parties',
    content: [
      {
        subtitle: 'AI Generation Providers',
        text: 'Your business profile inputs are sent to third-party AI model providers (such as OpenAI and image generation APIs) to produce ad copy and images. These providers process data under strict confidentiality agreements and do not use your data to train their models.',
      },
      {
        subtitle: 'Payment Processing',
        text: 'Payments are handled by Stripe, Inc. AdForge AI never stores your full credit card number. Stripe\'s privacy policy governs how your billing data is handled.',
      },
      {
        subtitle: 'Analytics & Monitoring',
        text: 'We use analytics tools to understand platform usage patterns. Data shared with analytics providers is aggregated and anonymized where possible.',
      },
      {
        subtitle: 'Legal Requirements',
        text: 'We may disclose your information if required by law, court order, or government authority, or if we believe disclosure is necessary to protect the rights, property, or safety of AdForge AI, our users, or the public.',
      },
    ],
  },
  {
    id: 'data-retention',
    title: '4. Data Retention',
    text: 'We retain your account data for as long as your account is active. If you delete your account, we will delete or anonymize your personal data within 30 days, except where retention is required by law or for legitimate business purposes (e.g., resolving disputes, fraud prevention). Generated ad content stored in your history is deleted upon account deletion.',
  },
  {
    id: 'security',
    title: '5. Security',
    text: 'We implement industry-standard security measures including encryption in transit (TLS), encryption at rest, access controls, and regular security reviews. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security. We encourage you to use a strong, unique password and enable any available account security features.',
  },
  {
    id: 'cookies',
    title: '6. Cookies & Tracking',
    content: [
      {
        subtitle: 'Essential Cookies',
        text: 'Required for the platform to function, including authentication and session management. These cannot be disabled.',
      },
      {
        subtitle: 'Analytics Cookies',
        text: 'Help us understand how users interact with AdForge AI so we can improve the product. You may opt out via your browser settings or a cookie consent tool.',
      },
      {
        subtitle: 'Marketing Cookies',
        text: 'Used to measure the effectiveness of our advertising campaigns. You may opt out via browser settings or industry opt-out tools.',
      },
    ],
  },
  {
    id: 'your-rights',
    title: '7. Your Rights',
    bullets: [
      'Access — Request a copy of the personal data we hold about you',
      'Correction — Ask us to correct inaccurate or incomplete data',
      'Deletion — Request deletion of your personal data ("right to be forgotten")',
      'Portability — Receive your data in a structured, machine-readable format',
      'Objection — Object to certain types of processing, including direct marketing',
      'Restriction — Ask us to restrict processing of your data in certain circumstances',
      'Withdraw Consent — Where processing is based on consent, withdraw it at any time',
    ],
    footer: `To exercise any of these rights, email us at ${CONTACT_EMAIL}. We will respond within 30 days.`,
  },
  {
    id: 'children',
    title: '8. Children\'s Privacy',
    text: 'AdForge AI is not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will promptly delete it.',
  },
  {
    id: 'international',
    title: '9. International Data Transfers',
    text: 'AdForge AI is operated from the United States. If you are accessing our service from outside the US, your data may be transferred to and processed in the US or other countries. We take appropriate steps to ensure such transfers comply with applicable data protection laws, including using standard contractual clauses where required.',
  },
  {
    id: 'changes',
    title: '10. Changes to This Policy',
    text: 'We may update this Privacy Policy from time to time. When we do, we will update the "Last Updated" date at the top of this page and, for material changes, notify you via email or an in-app notice. Continued use of AdForge AI after changes take effect constitutes acceptance of the updated policy.',
  },
  {
    id: 'contact',
    title: '11. Contact Us',
    text: `If you have questions, concerns, or requests regarding this Privacy Policy, please contact us at ${CONTACT_EMAIL} or write to us at: AdForge AI, Privacy Team, ${WEBSITE}`,
  },
];

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="bg-stone-950 pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-stone-500 hover:text-orange-400 text-sm transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-stone-600 text-sm" />
            <span className="text-stone-400 text-sm">Privacy Policy</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">Privacy Policy</h1>
          <p className="text-stone-400 text-base">
            Last updated: <span className="text-orange-400 font-semibold">{LAST_UPDATED}</span>
          </p>
          <p className="text-stone-400 text-sm mt-4 max-w-2xl leading-relaxed">
            At {COMPANY}, we take your privacy seriously. This policy explains what data we collect,
            why we collect it, how we use it, and your rights regarding your information.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-12">

          {/* Sidebar TOC */}
          <aside className="lg:w-56 shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4">On this page</p>
              <nav className="flex flex-col gap-1">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="text-sm text-stone-500 hover:text-orange-500 transition-colors py-1 leading-snug cursor-pointer"
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0">
            <div className="space-y-12">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-28">
                  <h2 className="text-xl font-bold text-stone-900 mb-4 pb-3 border-b border-stone-100">
                    {section.title}
                  </h2>

                  {/* Plain text */}
                  {'text' in section && section.text && (
                    <p className="text-stone-600 leading-relaxed text-[15px]">{section.text}</p>
                  )}

                  {/* Bullet list */}
                  {'bullets' in section && section.bullets && (
                    <>
                      <ul className="space-y-2.5">
                        {section.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-3">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                            <span className="text-stone-600 text-[15px] leading-relaxed">{b}</span>
                          </li>
                        ))}
                      </ul>
                      {'footer' in section && section.footer && (
                        <p className="mt-4 text-stone-500 text-sm italic">{section.footer}</p>
                      )}
                    </>
                  )}

                  {/* Sub-sections */}
                  {'content' in section && section.content && (
                    <div className="space-y-5">
                      {section.content.map((item) => (
                        <div key={item.subtitle} className="bg-stone-50 rounded-xl p-5">
                          <h3 className="text-sm font-bold text-stone-800 mb-2">{item.subtitle}</h3>
                          <p className="text-stone-600 text-[15px] leading-relaxed">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-16 bg-stone-950 rounded-2xl p-8 text-center">
              <p className="text-white font-semibold text-lg mb-2">Have questions about your privacy?</p>
              <p className="text-stone-400 text-sm mb-6">Our team is happy to help clarify anything in this policy.</p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line" />
                Contact Privacy Team
              </a>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
